<ng-container *ngIf="state$ | async as state">
  <h2 mat-dialog-title>
    {{
      (selectionType === SelectionType.PRODUCT
        ? 'ACTION_LISTS.ACTIVATE_PRODUCT_DIALOG.TITLE'
        : 'ACTION_LISTS.ACTIVATE_ADDON_DIALOG.TITLE'
      ) | translate : { name: actionList.name }
    }}
  </h2>
  <mat-dialog-content class="dialog--overflow">
    <inventory-ui-item-search-single-select-filter
      [config]="{ partnerId: state.partnerId, filters: productFilters }"
      (selectionChange)="selectProduct($event)"
    ></inventory-ui-item-search-single-select-filter>
    <div *ngIf="selectionType === SelectionType.PRODUCT && state.selectedProductHasEditions">
      <mat-form-field floatLabel="always">
        <mat-label>
          {{ 'ACTION_LISTS.ACTIVATE_PRODUCT_DIALOG.SELECT_AN_EDITION' | translate }}
        </mat-label>
        <mat-select disableOptionCentering width="230" (selectionChange)="selectEdition($event.value)">
          <mat-option *ngFor="let edition of state.selectedProductEditions" [value]="edition">
            {{ state.selectedProduct.name + ' ' + edition.editionName }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <inventory-ui-item-search-single-select-filter
      *ngIf="selectionType === SelectionType.ADDON && state.selectedProduct && state.selectedProductHasAddons"
      [config]="{ partnerId: state.partnerId, filters: state.addonFilters }"
      (selectionChange)="selectAddon($event)"
    ></inventory-ui-item-search-single-select-filter>
    <ng-container
      *ngTemplateOutlet="
        selectionType === SelectionType.PRODUCT ? productWarning : addonWarning;
        context: { state: state }
      "
    ></ng-container>
  </mat-dialog-content>
  <mat-dialog-actions>
    <button
      mat-raised-button
      color="primary"
      (click)="activateItem()"
      [disabled]="!state.isButtonEnabled || previewLoading || buttonLoading"
    >
      <ng-container *ngIf="!buttonLoading; else matSpinner">Activate</ng-container>
      <ng-template #matSpinner>
        <mat-spinner [diameter]="30"></mat-spinner>
      </ng-template>
    </button>
    <button mat-button matDialogClose tabindex="-1" class="close-button">Cancel</button>
  </mat-dialog-actions>
</ng-container>

<ng-template #productWarning let-state="state">
  <div *ngIf="state.selectedProduct" class="alert-warning">
    <div>
      <mat-spinner
        [strokeWidth]="4"
        [diameter]="24"
        *ngIf="state.selectedProductHasEditions === null || previewLoading"
      ></mat-spinner>
    </div>
    <div [ngClass]="{ hidden: state.selectedProductHasEditions === null || previewLoading }">
      <p *ngIf="state.numActiveAccounts === actionList.accounts">
        All accounts on this list already have
        <strong>{{ state.selectedProduct.name }}</strong>
        activated.
      </p>
      <ng-container *ngIf="state.numActiveAccounts !== actionList.accounts">
        <p *ngIf="state.numActiveAccounts === 0">
          Would you like to activate
          <strong>
            {{ state.selectedProductDisplayName }}
          </strong>
          for {{ state.numAccountsToActivate }} account{{ state.numAccountsToActivate > 1 ? 's' : '' }}
          in this list?
        </p>
        <p *ngIf="state.numActiveAccounts !== 0">
          <strong>{{ state.selectedProduct.name }}</strong>
          is already active for {{ state.numActiveAccounts }} of {{ actionList.accounts }} account{{
            actionList.accounts > 1 ? 's' : ''
          }}
          in this list. Would you like to activate
          {{ state.selectedProductDisplayName }}
          for the remaining {{ state.numAccountsToActivate }} account{{ state.numAccountsToActivate > 1 ? 's' : '' }}?
        </p>
      </ng-container>
      <div [ngClass]="{ hidden: state.hidePricing }">
        <div class="pricing" *ngIf="state.purchaseCost as pricing; else displayGenericPricing">
          <div>
            {{ 'ACTION_LISTS.ACTIVATE_PRODUCT_DIALOG.ESTIMATED_COST' | translate }}
          </div>
          <ng-container *ngIf="pricing">
            <div>
              <div>
                {{
                  pricing.subtotalCost - pricing.discountAmount | price : pricing.currency : pricing.billingFrequency
                }}
              </div>
              <div class="commitment-string" *ngIf="pricing.initialCommitment > 1 || pricing.recurringCommitment > 1">
                {{ pricing.initialCommitment }}
                {{ getFrequencyNoun(pricing.billingFrequency) }} minimum, renews for {{ pricing.recurringCommitment }}
                {{ getFrequencyNoun(pricing.billingFrequency) }} periods
              </div>
              <div *ngIf="pricing.setupFee > 0">+ {{ pricing.setupFee | price : pricing.currency }} setup fee</div>
            </div>
          </ng-container>
        </div>
        <ng-template #displayGenericPricing>
          <ng-container *ngIf="!previewLoading">
            <p>
              *
              {{ 'ACTION_LISTS.ACTIVATE_PRODUCT_DIALOG.STANDARD_FEES_APPLY' | translate }}
            </p>
          </ng-container>
        </ng-template>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #addonWarning let-state="state">
  <div *ngIf="state.selectedProductHasAddons === false" class="alert-warning">
    <p>
      {{ 'ACTION_LISTS.ACTIVATE_ADDON_DIALOG.NO_ADDONS_AVAILABLE' | translate }}
    </p>
  </div>
  <div *ngIf="state.selectedAddon" class="alert-warning">
    <div>
      <mat-spinner *ngIf="previewLoading" [strokeWidth]="4" [diameter]="24"></mat-spinner>
    </div>
    <div *ngIf="state.selectedAddonData" [ngClass]="{ hidden: previewLoading }">
      <p *ngIf="state.numActiveAccounts === actionList.accounts && !state.selectedAddonData.multipleActivations">
        All accounts on this list have
        <strong>{{ state.selectedProduct.name }}</strong>
        activated. Would you like to activate
        <strong>{{ state.selectedAddon.name }}</strong>
        for the {{ state.numActiveAccounts }} account{{ state.numActiveAccounts > 1 ? 's' : '' }}
        where it is not already active?
      </p>
      <p *ngIf="state.numActiveAccounts === actionList.accounts && state.selectedAddonData.multipleActivations">
        All accounts on this list have
        <strong>{{ state.selectedProduct.name }}</strong>
        activated. Would you like to activate
        <strong>{{ state.selectedAddon.name }}</strong>
        for the {{ state.numActiveAccounts }} account{{ state.numActiveAccounts > 1 ? 's' : '' }}?
      </p>
      <ng-container *ngIf="state.numActiveAccounts !== actionList.accounts">
        <p *ngIf="state.numActiveAccounts === 0">
          <strong>{{ state.selectedProduct.name }}</strong>
          is not active for any accounts on this list. Activate it to activate
          <strong>{{ state.selectedAddon.name }}</strong>
          for these accounts.
        </p>
        <p *ngIf="state.numActiveAccounts !== 0 && !state.selectedAddonData.multipleActivations">
          <strong>{{ state.selectedProduct.name }}</strong>
          is active for {{ state.numActiveAccounts }} of {{ actionList.accounts }} account{{
            actionList.accounts > 1 ? 's' : ''
          }}
          on this list. Would you like to activate
          <strong>{{ state.selectedAddon.name }}</strong>
          for the {{ state.numActiveAccounts }} account{{ state.numActiveAccounts > 1 ? 's' : '' }}
          where it is not already active?
        </p>
        <p *ngIf="state.numActiveAccounts !== 0 && state.selectedAddonData.multipleActivations">
          <strong>{{ state.selectedProduct.name }}</strong>
          is active for {{ state.numActiveAccounts }} of {{ actionList.accounts }} account{{
            actionList.accounts > 1 ? 's' : ''
          }}
          on this list. Would you like to activate
          <strong>{{ state.selectedAddon.name }}</strong>
          for the {{ state.numActiveAccounts }} account{{ state.numActiveAccounts > 1 ? 's' : '' }}?
        </p>
      </ng-container>
      <p *ngIf="state.numActiveAccounts !== 0 && state.selectedAddonData.multipleActivations">
        <strong>{{ state.selectedAddon.name }}</strong>
        can be activated multiple times for a single account. Performing this action will activate
        <strong>{{ state.selectedAddon.name }}</strong>
        again even if it is already active on the account.
      </p>
      <div [ngClass]="{ hidden: state.hidePricing }">
        <div class="pricing" *ngIf="state.purchaseCost as pricing; else displayGenericPricing">
          <div>
            Estimated cost for the activation of
            {{ state.numActiveAccounts }} add-on{{ state.numActiveAccounts > 1 ? 's' : '' }}:
          </div>
          <ng-container *ngIf="pricing">
            <div>
              {{ pricing.subtotalCost - pricing.discountAmount | price : pricing.currency : pricing.billingFrequency }}
            </div>
            <div *ngIf="pricing.setupFee > 0">+ {{ pricing.setupFee | price : pricing.currency }} setup fee</div>
          </ng-container>
        </div>
        <ng-template #displayGenericPricing>
          <ng-container *ngIf="!previewLoading && state.numActiveAccounts !== 0">
            <p>* Standard monthly fees and commitments apply.</p>
          </ng-container>
        </ng-template>
      </div>
    </div>
  </div>
</ng-template>
